import moment from 'moment';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import helpModeService from 'services/helpModeService';

const QUERY_KEY_PREFIX = ['help-mode', 'videos'];
const LAST_WATCHED_TIME_KEY = 'paperlessHelpModeLastWatchedTime';

/**
 * Adapter that treats 404s from the API as valid empty cases.
 *
 * The semantically correct API response for not found is a 404.
 * For help mode, a 404 for a page indicates that there is no help video.
 * This typically results in the service function raising an exception.
 * This wrapper instead converts those 404 responses to valid null responses.
 *
 * @param queryFn A query function which may throw a 404
 * @returns {*} A new query function identical to the input queryFn, except with 404s converted to nulls
 */
const notFoundAsNull = (queryFn) => async () => {
  try {
    return await queryFn();
  } catch (e) {
    if (e?.statusCode === 404) {
      return null;
    }

    throw e;
  }
};

export const useReleaseVideosQuery = (options = {}) =>
  useQuery(
    [...QUERY_KEY_PREFIX, 'release'],
    notFoundAsNull(() => helpModeService.getReleaseVideos()),
    options,
  );

export const usePageVideosQuery = (pageId, options = {}) =>
  useQuery(
    [...QUERY_KEY_PREFIX, 'page-level', pageId],
    notFoundAsNull(() => helpModeService.getPageLevelVideos(pageId)),
    options,
  );

export const useAdditionalResourceVideosQuery = (pageId, options = {}) =>
  useQuery(
    [...QUERY_KEY_PREFIX, 'additional-resources', pageId],
    notFoundAsNull(() => helpModeService.getAdditionalResourceVideos(pageId)),
    options,
  );

export const useViewAndRateMutation = ({ onSuccess, ...options } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ videoId, secondsLapsed, rating }) =>
      helpModeService.viewAndRateVideo(videoId, {
        secondsLapsed,
        rating,
      }),
    {
      ...options,
      onSuccess: async (...args) => {
        await queryClient.invalidateQueries(QUERY_KEY_PREFIX);
        onSuccess && onSuccess(...args);
      },
    },
  );
};

export const useHelpModeLastRequiredWatchedTime = (saveTrigger) => {
  useEffect(() => {
    if (saveTrigger) {
      window.localStorage.setItem(
        LAST_WATCHED_TIME_KEY,
        moment().toISOString(),
      );
    }
  }, [saveTrigger]);
  return moment(window.localStorage.getItem(LAST_WATCHED_TIME_KEY));
};
